import { AlertTriangle, PackageMinus, ArrowLeftRight, Trash2, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/format";
import { useIsMobile } from "@/components/shared/PagePrimitives";

const fmtQty = (n) => (n == null ? "0" : Number(n).toLocaleString("cs-CZ", { maximumFractionDigits: 3 }));

// Akce řádku: Vydat + ikony 40×40; sklad bez oprávnění dostane „Jen náhled"
// a zašedlé ikony (ne skryté).
function RowActions({ it, op, empty, onIssue, onTransfer, onWriteoff, onMin }) {
  if (!op) return (
    <>
      <span className="text-xs font-semibold text-muted-foreground bg-secondary rounded-full px-3 py-1.5" data-testid={`readonly-${it.id}`}>Jen náhled</span>
      <Button size="icon" variant="ghost" className="w-10 h-10 opacity-40" disabled title="Převod"><ArrowLeftRight size={15} /></Button>
      <Button size="icon" variant="ghost" className="w-10 h-10 opacity-40" disabled title="Odpis"><Trash2 size={15} /></Button>
      <Button size="icon" variant="ghost" className="w-10 h-10 opacity-40" disabled title="Minimální zásoba"><Gauge size={15} /></Button>
    </>
  );
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-full" disabled={empty} onClick={() => onIssue(it)} data-testid={`issue-btn-${it.id}`}>
        <PackageMinus size={15} className="mr-1" /> Vydat
      </Button>
      <Button size="icon" variant="ghost" className="w-10 h-10" disabled={empty} onClick={() => onTransfer(it)} data-testid={`transfer-btn-${it.id}`} title="Převod"><ArrowLeftRight size={15} /></Button>
      <Button size="icon" variant="ghost" className="w-10 h-10" disabled={empty} onClick={() => onWriteoff(it)} data-testid={`writeoff-btn-${it.id}`} title="Odpis"><Trash2 size={15} /></Button>
      <Button size="icon" variant="ghost" className="w-10 h-10" onClick={() => onMin(it)} data-testid={`min-btn-${it.id}`} title="Minimální zásoba"><Gauge size={15} /></Button>
    </>
  );
}

const LowBadge = ({ id }) => (
  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-500 inline-flex items-center gap-1 shrink-0" data-testid={`low-badge-${id}`}>
    <AlertTriangle size={11} /> dochází
  </span>
);

// Tab „Zásoby" (§7). Desktop = tabulka (Položka / Skladem / Min. / JC Ø /
// Hodnota / akce), mobil = karty pod sebou. Cenové sloupce jen s `money`
// (stavbyvedoucí je nevidí vůbec).
export function StockList({ items, showWarehouse, money, canOp, onIssue, onTransfer, onWriteoff, onMin }) {
  const isMobile = useIsMobile();
  if (!items.length) return <div className="p-8 text-center text-muted-foreground text-sm" data-testid="stock-empty">Žádné položky.</div>;

  const rows = items.map((it) => ({
    it,
    low: (it.min_qty || 0) > 0 && (it.qty || 0) < it.min_qty,
    value: it.avg_price != null ? (it.qty || 0) * (it.avg_price || 0) : null,
    op: canOp ? canOp(it.warehouse_id) : true,
    empty: (it.qty || 0) <= 0,
  }));

  if (isMobile) return (
    <div className="divide-y divide-border" data-testid="stock-list">
      {rows.map(({ it, low, value, op, empty }) => (
        <div key={it.id} className="px-4 py-3 flex flex-col gap-2" data-testid={`stock-item-${it.id}`}>
          <div className="min-w-0">
            <div className="font-semibold truncate flex items-center gap-2">
              {it.name}
              {low && <LowBadge id={it.id} />}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {showWarehouse && it.warehouse_name && <span className="text-foreground/70">{it.warehouse_name} · </span>}
              <span className={`font-bold ${low ? "text-amber-600 dark:text-amber-500" : "text-foreground"}`}>Skladem {fmtQty(it.qty)} {it.unit}</span>
              {(it.min_qty || 0) > 0 && <> · min. {fmtQty(it.min_qty)} {it.unit}</>}
              {money && it.avg_price != null && <> · JC Ø {formatMoney(it.avg_price)}</>}
              {money && value != null && <> · hodnota {formatMoney(value)}</>}
            </div>
          </div>
          <div className="flex items-center gap-1"><RowActions it={it} op={op} empty={empty} onIssue={onIssue} onTransfer={onTransfer} onWriteoff={onWriteoff} onMin={onMin} /></div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="overflow-x-auto" data-testid="stock-list">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
            <th className="text-left font-bold px-4 py-2.5">Položka</th>
            {showWarehouse && <th className="text-left font-bold px-3 py-2.5">Sklad</th>}
            <th className="text-right font-bold px-3 py-2.5">Skladem</th>
            <th className="text-right font-bold px-3 py-2.5">Min.</th>
            {money && <th className="text-right font-bold px-3 py-2.5">JC Ø</th>}
            {money && <th className="text-right font-bold px-3 py-2.5">Hodnota</th>}
            <th className="px-4 py-2.5" aria-label="Akce"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map(({ it, low, value, op, empty }) => (
            <tr key={it.id} className="hover:bg-secondary/40 transition-colors" data-testid={`stock-item-${it.id}`}>
              <td className="px-4 py-2.5 max-w-[320px]">
                <div className="font-semibold truncate flex items-center gap-2">{it.name}{low && <LowBadge id={it.id} />}</div>
              </td>
              {showWarehouse && <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{it.warehouse_name || "—"}</td>}
              <td className={`px-3 py-2.5 text-right whitespace-nowrap tabular-nums font-bold ${low ? "text-amber-600 dark:text-amber-500" : ""}`}>{fmtQty(it.qty)} {it.unit}</td>
              <td className="px-3 py-2.5 text-right whitespace-nowrap tabular-nums text-muted-foreground">{(it.min_qty || 0) > 0 ? `${fmtQty(it.min_qty)} ${it.unit}` : "—"}</td>
              {money && <td className="px-3 py-2.5 text-right whitespace-nowrap tabular-nums text-muted-foreground">{it.avg_price != null ? formatMoney(it.avg_price) : "—"}</td>}
              {money && <td className="px-3 py-2.5 text-right whitespace-nowrap tabular-nums">{value != null ? formatMoney(value) : "—"}</td>}
              <td className="px-4 py-2.5">
                <div className="flex items-center justify-end gap-1"><RowActions it={it} op={op} empty={empty} onIssue={onIssue} onTransfer={onTransfer} onWriteoff={onWriteoff} onMin={onMin} /></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
